// Real HTTP adapter for FEATURE-044/047 (Admin User Management / Root Transfer) — talks to admin-api at
// /api/v1/admin/admins. Selected over the in-memory mock when VITE_USE_MOCK_API=false (see admin.adapter.ts);
// the production/docker env files set that flag, so this is the live deployment path.
// Maps the server error envelope { error: { code, message, fields, meta } } to AdminApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  ADMIN_API_ERROR_CODES,
  AdminApiError,
  type Admin,
  type AdminApi,
  type AdminApiErrorCode,
  type InviteAdminInput,
  type ListAdminsParams,
  type ListAdminsResult,
  type RootTransferInput,
  type RootTransferResult,
  type UpdateAdminInput,
} from '../types/admin.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  // An AdminApiError thrown by a shape guard above must pass through untouched —
  // re-wrapping it would read `e.response` (undefined) and downgrade it to NETWORK.
  if (e instanceof AdminApiError) throw e;

  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never got an HTTP reply — offline, DNS, or timeout
  // (Error Standard §3). Classify as NETWORK so the caller shows its operation-specific
  // failure copy rather than a generic unknown-error string.
  if (!candidate.response) {
    throw new AdminApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response?.data?.error;
  // SHCT-685 — match the code against the known catalog instead of casting. A code the FE
  // does not model (the BE's INTERNAL_ERROR on a 5xx, a new server code) collapses to
  // UNKNOWN and its raw server text is dropped here, at the boundary, so no caller can
  // leak an internal exception string ("DB connection pool exhausted…") into a toast.
  const rawCode = payload?.code;
  const known = rawCode != null && ADMIN_API_ERROR_CODES.has(rawCode);
  throw new AdminApiError(
    known ? (rawCode as AdminApiErrorCode) : 'UNKNOWN',
    known ? (payload?.message ?? '알 수 없는 오류가 발생했습니다.') : '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const adminHttpApi: AdminApi = {
  async listAdmins(params: ListAdminsParams): Promise<ListAdminsResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<ListAdminsResult>>(endpoints.admins, {
        params,
      });
      const data = res.data?.data;
      if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
        throw new AdminApiError('UNKNOWN', '관리자 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async inviteAdmin(input: InviteAdminInput): Promise<Admin> {
    try {
      const res = await httpClient.post<ApiEnvelope<Admin>>(
        `${endpoints.admins}/invitations`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async resendInvitation(id: number): Promise<void> {
    try {
      await httpClient.post(`${endpoints.admins}/${id}/resend-invitation`);
    } catch (e) {
      mapError(e);
    }
  },

  async updateAdmin(id: number, input: UpdateAdminInput): Promise<Admin> {
    try {
      const res = await httpClient.patch<ApiEnvelope<Admin>>(
        `${endpoints.admins}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async resetPassword(id: number): Promise<void> {
    try {
      await httpClient.post(`${endpoints.admins}/${id}/password-reset`);
    } catch (e) {
      mapError(e);
    }
  },

  async listRootTransferCandidates(): Promise<Admin[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<Admin[]>>(
        `${endpoints.admins}/root-transfer/candidates`,
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new AdminApiError('UNKNOWN', '권한 위임 대상 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async rootTransfer(input: RootTransferInput): Promise<RootTransferResult> {
    try {
      const res = await httpClient.post<ApiEnvelope<RootTransferResult>>(
        `${endpoints.admins}/root-transfer`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },
};
